import { format, differenceInDays } from 'date-fns';
import { Transaction, Account } from '../types';
import { formatAppDate, formatAppTime } from './timezoneUtils';
import { parseLocalDate } from '../../lib/recurringUtils.js';
export { calculateNextOccurrence, getUpcomingOccurrences, parseLocalDate } from '../../lib/recurringUtils.js';

const isToday = (d: Date) => format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
const isYesterday = (d: Date) => differenceInDays(new Date(), d) === 1;
const isThisWeek = (d: Date) => {
  const now = new Date();
  const start = new Date(now); start.setDate(now.getDate() - now.getDay());
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return d >= start && d <= end;
};

export const getDateGroupLabel = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  if (isThisWeek(date)) return format(date, 'EEEE');
  const daysDiff = differenceInDays(new Date(), date);
  if (daysDiff < 7) return format(date, 'EEEE');
  if (daysDiff < 30) return format(date, 'MMM dd');
  return format(date, 'MMM dd, yyyy');
};

/** Matches account_balances: recurring templates do not affect balance. */
export const affectsAccountBalance = (t: Transaction): boolean => !t.is_recurring;

export function compareTransactionsChronological(a: Transaction, b: Transaction): number {
  const byDate = parseLocalDate(a.date).getTime() - parseLocalDate(b.date).getTime();
  if (byDate) return byDate;
  const byCreated = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  if (byCreated) return byCreated;
  return String(a.id).localeCompare(String(b.id));
}

/** Newest-first ledger + running balances aligned with calculated_balance rules. */
export function getAccountLedger(
  accountId: string,
  transactions: Transaction[],
  initialBalance: number
): { transactions: Transaction[]; balanceMap: Map<string, number> } {
  const chronological = transactions
    .filter(t => t.account_id === accountId && affectsAccountBalance(t))
    .sort(compareTransactionsChronological);
  const balanceMap = new Map<string, number>();
  let balance = Number(initialBalance) || 0;
  for (const tx of chronological) {
    balance += tx.type === 'income' ? tx.amount : -tx.amount;
    balanceMap.set(tx.id, balance);
  }
  return { transactions: chronological.slice().reverse(), balanceMap };
}

export const groupTransactionsByDate = (transactions: Transaction[]): [string, Transaction[]][] => {
  const groups: Record<string, Transaction[]> = {};
  transactions.forEach(t => {
    const label = getDateGroupLabel(parseLocalDate(t.date));
    if (!groups[label]) groups[label] = [];
    groups[label].push(t);
  });
  return Object.entries(groups).sort((a, b) =>
    parseLocalDate(b[1][0].date).getTime() - parseLocalDate(a[1][0].date).getTime()
  );
};

export const TRANSACTION_TYPES = ['income', 'expense'] as const;

export const COMMON_CATEGORIES = {
    income: ['Salary', 'Freelance', 'Investment', 'Gift', 'Refund', 'Other'],
    expense: ['Food', 'Transport', 'Entertainment', 'Shopping', 'Bills', 'Healthcare', 'Education', 'Other']
};

/**
 * Check if a transaction is a lend/borrow, repayment, or partial return transaction
 * These should be excluded from income/expense calculations as they don't represent
 * actual income or expenses, just temporary money movements.
 */
export const isLendBorrowTransaction = (transaction: Transaction): boolean => {
    // Check tags for lend/borrow related transactions
    const tags = transaction.tags || [];
    const lendBorrowTags = ['lend_borrow', 'repayment', 'partial_return', 'partial_repayment', 'settlement'];
    
    const hasLendBorrowTag = tags.some(tag => lendBorrowTags.includes(tag));
    
    // Check description for lend/borrow patterns (both initial and repayment)
    const description = (transaction.description || '').toLowerCase();
    const lendBorrowPatterns = [
        // Initial transactions
        'borrowed from',
        'lent to',
        // Repayment patterns
        'repayment',
        'partial return',
        'debt repayment',
        'loan repayment',
        'repayment to',
        'repayment from',
        'partial loan repayment'
    ];
    
    const matchesDescription = lendBorrowPatterns.some(pattern => description.includes(pattern));
    
    return hasLendBorrowTag || matchesDescription;
};

export const isBusinessInvestmentFundingExpense = (t: Transaction): boolean =>
    t.type === 'expense' && Boolean(t.business_investment_contract_id);

/** Income/expense rows that count toward summary cards and net (excludes lend/borrow, BI funding, and recurring templates). */
export const countsTowardIncomeExpenseSummaries = (t: Transaction): boolean =>
    !isLendBorrowTransaction(t) && !isBusinessInvestmentFundingExpense(t) && !t.is_recurring;

/** Tooltip for summary UIs that use countsTowardIncomeExpenseSummaries. */
export const INCOME_EXPENSE_NET_TOOLTIP =
    'Income minus expenses, excluding lend/borrow flows, business investment funding, and recurring templates.';

export const filterTransactions = (
    transactions: Transaction[],
    filters: {
        search: string;
        type: string;
        category: string;
        account: string;
        dateRange: {
            start: string;
            end: string;
        };
    },
    globalSearchTerm: string = ''
): Transaction[] => {
    return transactions.filter(transaction => {
        const searchTerm = globalSearchTerm || filters.search;
        if (searchTerm) {
            const matchesSearch = 
                transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                transaction.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (transaction.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
            
            if (!matchesSearch) return false;
        }
        
        if (filters.type !== 'all') {
            const isTransfer = transaction.tags?.includes('transfer');
            if (isTransfer || transaction.type !== filters.type) {
                return false;
            }
        }
        
        if (filters.category !== 'all' && transaction.category !== filters.category) {
            return false;
        }
        
        if (filters.account !== 'all' && transaction.account_id !== filters.account) {
            return false;
        }
        
        if (filters.dateRange.start || filters.dateRange.end) {
            const transactionDate = new Date(transaction.date);
            const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
            const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
            
            if (startDate && transactionDate < startDate) return false;
            if (endDate && transactionDate > endDate) return false;
        }
        
        const isTransfer = transaction.tags?.includes('transfer');
        if (isTransfer) return false;
        
        return true;
    });
};

export const sortTransactions = (
    transactions: Transaction[],
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null
): Transaction[] => {
    if (!sortConfig) return transactions;
    
    return [...transactions].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortConfig.key) {
            case 'date':
                aValue = new Date(a.date).getTime();
                bValue = new Date(b.date).getTime();
                break;
            case 'description':
                aValue = a.description.toLowerCase();
                bValue = b.description.toLowerCase();
                break;
            case 'category':
                aValue = a.category.toLowerCase();
                bValue = b.category.toLowerCase();
                break;
            case 'amount':
                aValue = a.amount;
                bValue = b.amount;
                break;
            case 'type':
                aValue = a.type.toLowerCase();
                bValue = b.type.toLowerCase();
                break;
            default:
                return 0;
        }
        
        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
};

export const calculateTransactionStats = (transactions: Transaction[], accounts: Account[]) => {
    const incomeTransactions = transactions.filter(t => 
        t.type === 'income' && countsTowardIncomeExpenseSummaries(t)
    );
    const expenseTransactions = transactions.filter(t => 
        t.type === 'expense' && countsTowardIncomeExpenseSummaries(t)
    );
    
    const totalIncome = incomeTransactions.reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = expenseTransactions.reduce((sum, t) => sum + t.amount, 0);
    const netAmount = totalIncome - totalExpenses;
    
    const currencies = [...new Set(accounts.map(a => a.currency))];
    const primaryCurrency = currencies[0] || 'USD';
    
    const averageAmount = transactions.length > 0 
        ? transactions.reduce((sum, t) => sum + t.amount, 0) / transactions.length 
        : 0;
    
    const accountStats = accounts.map(account => {
        const accountTransactions = transactions.filter(t => t.account_id === account.id);
        return {
            account,
            count: accountTransactions.length,
            total: accountTransactions.reduce((sum, t) => sum + t.amount, 0)
        };
    }).sort((a, b) => b.count - a.count);
    
    const mostActiveAccount = accountStats[0];
    
    const categoryBreakdown = transactions.reduce((acc, transaction) => {
        const category = transaction.category;
        if (!acc[category]) {
            acc[category] = { count: 0, total: 0 };
        }
        acc[category].count++;
        acc[category].total += transaction.amount;
        return acc;
    }, {} as Record<string, { count: number; total: number }>);
    
    return {
        totalIncome,
        totalExpenses,
        netAmount,
        transactionCount: transactions.length,
        incomeCount: incomeTransactions.length,
        expenseCount: expenseTransactions.length,
        averageAmount,
        primaryCurrency,
        mostActiveAccount,
        categoryBreakdown
    };
};

export const getAccountName = (accountId: string, accounts: Account[]): string => {
    const account = accounts.find(a => a.id === accountId);
    return account?.name || 'Unknown Account';
};

export const formatTransactionDate = (date: string, format: 'short' | 'long' | 'time' = 'short'): string => {
    switch (format) {
        case 'short':
            return formatAppDate(date);
        case 'long':
            return formatAppDate(date);
        case 'time':
            return formatAppTime(date);
        default:
            return formatAppDate(date);
    }
};

export const validateTransaction = (transaction: Partial<Transaction>): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!transaction.description?.trim()) {
        errors.push('Transaction description is required');
    }
    
    if (!transaction.amount || transaction.amount <= 0) {
        errors.push('Transaction amount must be greater than 0');
    }
    
    if (!transaction.category?.trim()) {
        errors.push('Transaction category is required');
    }
    
    if (!transaction.account_id) {
        errors.push('Account is required');
    }
    
    if (!transaction.date) {
        errors.push('Transaction date is required');
    }
    
    if (!transaction.type || !TRANSACTION_TYPES.includes(transaction.type as any)) {
        errors.push('Valid transaction type is required');
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

export const getTransactionTypeDisplayName = (type: string): string => {
    switch (type) {
        case 'income':
            return 'Income';
        case 'expense':
            return 'Expense';
        default:
            return type.charAt(0).toUpperCase() + type.slice(1);
    }
};

export const isRecurringTransaction = (transaction: Transaction): boolean => {
    return (transaction as any).is_recurring || false;
};

export const getRecurringFrequencyDisplayName = (frequency?: string): string => {
    if (!frequency) return 'Not recurring';
    
    switch (frequency.toLowerCase()) {
        case 'daily':
            return 'Daily';
        case 'weekly':
            return 'Weekly';
        case 'monthly':
            return 'Monthly';
        case 'yearly':
            return 'Yearly';
        default:
            return frequency.charAt(0).toUpperCase() + frequency.slice(1);
    }
};

export const exportTransactionsToCSV = (transactions: Transaction[], accounts: Account[]): string => {
    const headers = ['Date', 'Description', 'Category', 'Account', 'Type', 'Amount', 'Tags'];
    const csvData = transactions.map(transaction => {
        const account = accounts.find(a => a.id === transaction.account_id);
        return [
            formatTransactionDate(transaction.date),
            transaction.description,
            transaction.category,
            account?.name || 'Unknown',
            getTransactionTypeDisplayName(transaction.type),
            transaction.amount,
            (transaction.tags || []).join('; ')
        ];
    });
    
    return [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');
};

/** All-time summary for a single account (excludes lend/borrow and BI funding expenses) */
export const getAccountAllTimeSummary = (
    accountId: string,
    transactions: Transaction[]
): {
    totalIncome: number;
    totalExpenses: number;
    netChange: number;
    totalSaved: number;
    totalDonated: number;
    incomeCount: number;
    expenseCount: number;
    firstDate: string | null;
    lastDate: string | null;
    count: number;
} => {
    const accountTxs = transactions.filter(t => t.account_id === accountId && affectsAccountBalance(t));
    const income = accountTxs.filter(t => t.type === 'income' && countsTowardIncomeExpenseSummaries(t));
    const expenses = accountTxs.filter(t => t.type === 'expense' && countsTowardIncomeExpenseSummaries(t));
    const totalIncome = income.reduce((s, t) => s + t.amount, 0);
    const totalExpenses = expenses.reduce((s, t) => s + t.amount, 0);
    let totalSaved = 0, totalDonated = 0;
    income.forEach(t => {
        if (t.category === 'Savings') totalSaved += t.amount;
        else if (t.category === 'Donation') totalDonated += t.amount;
    });
    const sorted = [...accountTxs].sort(compareTransactionsChronological);
    return {
        totalIncome,
        totalExpenses,
        netChange: totalIncome - totalExpenses,
        totalSaved,
        totalDonated,
        incomeCount: income.length,
        expenseCount: expenses.length,
        firstDate: sorted[0]?.date ?? null,
        lastDate: sorted[sorted.length - 1]?.date ?? null,
        count: accountTxs.length
    };
};

export const getDateRangePresets = () => {
    const today = new Date();
    
    return {
        today: {
            start: today.toISOString().slice(0, 10),
            end: today.toISOString().slice(0, 10),
            label: 'Today'
        },
        week: {
            start: (() => {
                const day = today.getDay();
                const diffToMonday = (day === 0 ? -6 : 1) - day;
                const monday = new Date(today);
                monday.setDate(today.getDate() + diffToMonday);
                return monday.toISOString().slice(0, 10);
            })(),
            end: (() => {
                const day = today.getDay();
                const diffToMonday = (day === 0 ? -6 : 1) - day;
                const monday = new Date(today);
                monday.setDate(today.getDate() + diffToMonday);
                const sunday = new Date(monday);
                sunday.setDate(monday.getDate() + 6);
                return sunday.toISOString().slice(0, 10);
            })(),
            label: 'This Week'
        },
        month: {
            start: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10),
            end: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10),
            label: 'This Month'
        },
        lastMonth: {
            start: new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10),
            end: new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10),
            label: 'Last Month'
        },
        year: {
            start: new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10),
            end: new Date(today.getFullYear(), 11, 31).toISOString().slice(0, 10),
            label: 'This Year'
        }
    };
}; 

